import React from "react";
import { ArrowRight, CheckCircle, DollarSign, LogOut, PieChart, RefreshCw, Briefcase, Zap } from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import EosBackdrop from "../EosBackdrop";
import { useBrokerSelection } from "./useBrokerSelection";

const BrokerSelection: React.FC = () => {
  const {
    selectedBroker,
    setSelectedBroker,
    isUpdating,
    portfolioData,
    loadingPortfolio,
    accountBalance,
    brokers,
    handleChangeBroker,
    handleBrokerSelect,
    fetchAccountBalance,
    fetchPortfolioData,
    isConnected,
    shouldShowBrokerSelection,
  } = useBrokerSelection();

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden text-slate-100">
      <EosBackdrop />
      <Header />
      <div className="relative z-10 max-w-7xl mx-auto w-full flex-1 py-6 px-4 sm:px-6 lg:px-8">
        {isConnected ? (
          <>
            <div className="glass-card p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="bg-emerald-500/10 p-2 rounded-full mr-3">
                    <Zap className="h-6 w-6 text-emerald-300" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-slate-100">Portfolio Overview</h1>
                    <p className="text-sm text-slate-400">Connected to Zerodha</p>
                  </div>
                </div>
                <button
                  onClick={() => void handleChangeBroker()}
                  className="flex items-center px-4 py-2 bg-red-500/10 text-red-300 rounded-lg hover:bg-red-500/20 transition-colors"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Change Broker
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="glass-card p-4">
                <div className="flex items-center">
                  <div className="bg-green-500/10 p-2 rounded-lg mr-3">
                    <DollarSign className="h-5 w-5 text-green-300" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Available Balance</p>
                    <p className="text-lg font-bold text-slate-100">
                      {accountBalance !== null ? `₹${accountBalance.toLocaleString("en-IN")}` : "Loading..."}
                    </p>
                    <button
                      onClick={() => void fetchAccountBalance()}
                      className="text-xs text-green-400 hover:text-green-300 mt-1"
                    >
                      <RefreshCw className="h-3 w-3 inline mr-1" />
                      Refresh
                    </button>
                  </div>
                </div>
              </div>

              <div className="glass-card p-4">
                <div className="flex items-center">
                  <div className="bg-accent-500/10 p-2 rounded-lg mr-3">
                    <PieChart className="h-5 w-5 text-accent-300" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Total Holdings</p>
                    <p className="text-lg font-bold text-slate-100">
                      {portfolioData ? portfolioData.length : "Loading..."}
                    </p>
                  </div>
                </div>
              </div>

              <div className="glass-card p-4">
                <div className="flex items-center">
                  <div className="bg-purple-500/10 p-2 rounded-lg mr-3">
                    <Briefcase className="h-5 w-5 text-purple-300" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Portfolio Value</p>
                    <p className="text-lg font-bold text-slate-100">
                      {portfolioData
                        ? `₹${portfolioData.reduce((total, holding) => total + (holding.last_price ?? 0) * (holding.quantity ?? 0), 0).toLocaleString("en-IN")}`
                        : "Loading..."}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10 bg-white/5">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-100">Holdings</h2>
                    <p className="text-sm text-slate-400">Current portfolio positions</p>
                  </div>
                  <button
                    onClick={() => void fetchPortfolioData()}
                    className="flex items-center text-emerald-400 hover:text-emerald-300 transition-colors"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </button>
                </div>
              </div>

              {loadingPortfolio ? (
                <div className="p-8 text-center text-slate-400">Loading holdings...</div>
              ) : portfolioData && portfolioData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-white/10">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Symbol
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Last Price
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {portfolioData.map((holding, index) => (
                        <tr key={`${holding.symbol ?? "holding"}-${index}`}>
                          <td className="px-4 py-3 text-sm font-medium text-slate-100">{holding.symbol ?? "N/A"}</td>
                          <td className="px-4 py-3 text-sm text-slate-400">{holding.quantity ?? 0}</td>
                          <td className="px-4 py-3 text-sm text-slate-400">
                            ₹{(holding.last_price ?? 0).toLocaleString("en-IN")}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-400">
                            ₹{((holding.last_price ?? 0) * (holding.quantity ?? 0)).toLocaleString("en-IN")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-slate-400">No holdings available.</div>
              )}
            </div>
          </>
        ) : shouldShowBrokerSelection ? (
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-slate-100 mb-3">Connect Your Broker</h1>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                Choose a broker and connect your account to start trading with Engineering of Stocks.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {brokers.map((broker) => {
                const Icon = broker.icon;
                return (
                  <button
                    key={broker.id}
                    onClick={() => setSelectedBroker(broker.id)}
                    className={`p-5 rounded-2xl border text-left transition-all duration-200 ${selectedBroker === broker.id ? "border-emerald-400 bg-emerald-500/10 shadow-lg" : "border-white/15 bg-white/5 hover:border-emerald-400/50 hover:shadow-md"}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div
                        className={`w-12 h-12 rounded-xl bg-gradient-to-r ${broker.color} flex items-center justify-center text-white`}
                      >
                        <Icon className={`h-6 w-6 ${broker.iconColor}`} />
                      </div>
                      {broker.popular && (
                        <span className="px-2 py-1 text-xs font-semibold text-emerald-300 bg-emerald-500/15 rounded-full">
                          Popular
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">{broker.name}</h3>
                    <p className="text-sm text-slate-400 mt-2">{broker.description}</p>
                  </button>
                );
              })}
            </div>

            <div className="glass-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-100">Selected Broker</h2>
                  <p className="text-sm text-slate-400">
                    {selectedBroker
                      ? brokers.find((broker) => broker.id === selectedBroker)?.name
                      : "No broker selected yet"}
                  </p>
                </div>
                <button
                  onClick={() => void handleBrokerSelect()}
                  disabled={!selectedBroker || isUpdating}
                  className={`flex items-center px-6 py-3 rounded-lg font-semibold transition-colors ${!selectedBroker || isUpdating ? "bg-white/10 text-slate-500 cursor-not-allowed" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
                >
                  {isUpdating ? "Connecting..." : "Continue"}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/10 rounded-full mb-4">
              <CheckCircle className="h-8 w-8 text-emerald-300" />
            </div>
            <h2 className="text-2xl font-bold text-slate-100 mb-2">Connecting to broker...</h2>
            <p className="text-slate-400">Please wait while we complete the connection process.</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default BrokerSelection;
